import {
  ActionIcon,
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Collapse,
  Group,
  Stack,
  Text,
  Textarea,
  Tooltip,
  UnstyledButton,
} from '@mantine/core'
import { openConfirmModal } from '@mantine/modals'
import { IconChevronDown, IconChevronUp, IconCopy } from '@/components/icons'
import { identifierTypeLabel } from '@/lib/identifier-labels'
import { formatDateTimeDot, formatRelativeAgoRu } from '@/lib/ticket-display'
import type { DisplayIdent } from '@/components/tickets/ticket-detail-page-helpers'
import type { CustomerRow, TicketDetailTicket } from '@/types/api'

type TicketCustomerInfoCardProps = {
  ticket: TicketDetailTicket
  customer: CustomerRow | null
  avatarSrc?: string | null
  customerName: string
  sidebarEmail: string
  identifiers: DisplayIdent[]
  noteEditing: boolean
  noteDraft: string
  noteSaving: boolean
  collapsed?: boolean
  onToggleCollapsed?: () => void
  onOpenAvatar: () => void
  onOpenIdentifiers: () => void
  onCopy: (value: string) => void
  onChangeNoteDraft: (value: string) => void
  onSetNoteEditing: (editing: boolean) => void
  onSaveNote: () => void
}

export function TicketCustomerInfoCard({
  ticket,
  customer,
  avatarSrc,
  customerName,
  sidebarEmail,
  identifiers,
  noteEditing,
  noteDraft,
  noteSaving,
  collapsed = false,
  onToggleCollapsed,
  onOpenAvatar,
  onOpenIdentifiers,
  onCopy,
  onChangeNoteDraft,
  onSetNoteEditing,
  onSaveNote,
}: TicketCustomerInfoCardProps) {
  const requestSaveNote = () => {
    openConfirmModal({
      title: 'Сохранить примечание?',
      children: (
        <Text size="sm" c="dimmed">
          Текст примечания к карточке клиента будет записан в профиль.
        </Text>
      ),
      labels: { confirm: 'Сохранить', cancel: 'Отмена' },
      onConfirm: onSaveNote,
    })
  }

  return (
    <Card withBorder padding="sm" radius="md">
      <Group justify="space-between" gap="xs" wrap="nowrap" mb={collapsed ? 0 : 8}>
        <Box style={{ minWidth: 0 }}>
          <Text fw={600} size="xs">
            Информация о клиенте
          </Text>
          {collapsed ? (
            <Text size="xs" c="dimmed" truncate>
              {customerName}
              {sidebarEmail ? ` · ${sidebarEmail}` : ''}
            </Text>
          ) : null}
        </Box>
        {onToggleCollapsed ? (
          <Tooltip label={collapsed ? 'Развернуть' : 'Свернуть'} withArrow>
            <ActionIcon
              size="sm"
              variant="subtle"
              onClick={onToggleCollapsed}
              aria-label="Свернуть информацию о клиенте"
            >
              {collapsed ? <IconChevronDown size={14} stroke={1.5} /> : <IconChevronUp size={14} stroke={1.5} />}
            </ActionIcon>
          </Tooltip>
        ) : null}
      </Group>
      <Collapse in={!collapsed}>
        <Group gap="sm" wrap="nowrap" align="flex-start" mb={8}>
          <UnstyledButton
            type="button"
            onClick={() => customer?.avatar_url && onOpenAvatar()}
            style={{ flexShrink: 0, cursor: customer?.avatar_url ? 'pointer' : 'default' }}
          >
            <Avatar src={avatarSrc} radius="xl" size="md">
              {(customerName || '?').charAt(0)}
            </Avatar>
          </UnstyledButton>
          <Box style={{ minWidth: 0, flex: 1 }}>
            <Group gap={4} wrap="nowrap" align="center">
              <Text fw={600} size="sm" lh={1.3} style={{ flex: 1, minWidth: 0 }} truncate>
                {customerName}
              </Text>
              {customerName && customerName !== '—' ? (
                <Tooltip label="Копировать имя" withArrow>
                  <ActionIcon
                    size="xs"
                    variant="subtle"
                    aria-label="Копировать имя клиента"
                    onClick={() => onCopy(customerName)}
                  >
                    <IconCopy size={12} stroke={1.5} />
                  </ActionIcon>
                </Tooltip>
              ) : null}
            </Group>
            {ticket.customer_external_id ? (
              <Group gap={4} wrap="nowrap" mt={2}>
                <Text size="xs" c="dimmed">
                  ({ticket.customer_external_id})
                </Text>
                <Tooltip label="Копировать ID" withArrow>
                  <ActionIcon
                    size="xs"
                    variant="subtle"
                    aria-label="Копировать внешний ID"
                    onClick={() => onCopy(ticket.customer_external_id || '')}
                  >
                    <IconCopy size={12} stroke={1.5} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            ) : null}
            {sidebarEmail ? (
              <Group gap={4} wrap="nowrap" mt={4}>
                <Text size="xs" c="dimmed" truncate style={{ flex: 1 }}>
                  {sidebarEmail}
                </Text>
                <Tooltip label="Копировать email" withArrow>
                  <ActionIcon
                    size="xs"
                    variant="subtle"
                    aria-label="Копировать email"
                    onClick={() => onCopy(sidebarEmail)}
                  >
                    <IconCopy size={12} stroke={1.5} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            ) : null}
          </Box>
        </Group>
        <Text size="xs" c="dimmed" mb={2}>
          Создано:{' '}
          {ticket.created_at ? (
            <>
              {formatDateTimeDot(ticket.created_at)}
              {(() => {
                const r = formatRelativeAgoRu(ticket.created_at)
                return r ? ` (${r})` : ''
              })()}
            </>
          ) : (
            '—'
          )}
        </Text>
        {customer?.created_at ? (
          <Text size="xs" c="dimmed" mb={8}>
            Клиент в системе: {formatDateTimeDot(customer.created_at)}
          </Text>
        ) : (
          <Box mb={8} />
        )}

        {identifiers.length > 0 ? (
          <Box mb={8}>
            <Text fw={600} size="xs" mb={6}>
              Данные клиента
            </Text>
            <Group gap={6} align="flex-start">
              {identifiers.map((ident, idx) => {
                const label = identifierTypeLabel(ident.identifier_type)
                return (
                  <Group
                    key={`${ident.identifier_type}-${idx}-${ident.identifier_value}`}
                    gap={4}
                    wrap="nowrap"
                    align="center"
                    style={{
                      background: 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-6))',
                      border: '1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))',
                      padding: '4px 8px',
                      borderRadius: 6,
                      maxWidth: '100%',
                    }}
                  >
                    <Text size="xs" style={{ wordBreak: 'break-all', flex: 1, minWidth: 0 }}>
                      {label}: {ident.identifier_value}
                    </Text>
                    {ident.identifier_value ? (
                      <Tooltip label={`Копировать: ${label}`} withArrow>
                        <ActionIcon
                          size="xs"
                          variant="subtle"
                          aria-label={`Копировать ${label}`}
                          onClick={() => onCopy(ident.identifier_value)}
                        >
                          <IconCopy size={12} stroke={1.5} />
                        </ActionIcon>
                      </Tooltip>
                    ) : null}
                  </Group>
                )
              })}
            </Group>
          </Box>
        ) : null}

        {ticket.customer_id ? (
          <Button size="compact-xs" variant="light" fullWidth mb={8} onClick={onOpenIdentifiers}>
            Данные клиента
          </Button>
        ) : null}

        {noteEditing ? (
          <Stack gap="xs">
            <Textarea
              size="xs"
              label="Примечание"
              value={noteDraft}
              onChange={(e) => onChangeNoteDraft(e.currentTarget.value)}
              minRows={2}
              maxLength={1000}
            />
            <Group justify="flex-end" gap={6}>
              <Button size="compact-xs" variant="default" onClick={() => onSetNoteEditing(false)}>
                Отмена
              </Button>
              <Button size="compact-xs" loading={noteSaving} onClick={requestSaveNote}>
                Сохранить
              </Button>
            </Group>
          </Stack>
        ) : (
          <>
            {customer?.note ? (
              <Alert color="orange" variant="light" p="xs" mb={6}>
                <Text size="xs" style={{ whiteSpace: 'pre-wrap' }}>
                  {customer.note}
                </Text>
                <Button
                  size="compact-xs"
                  variant="subtle"
                  mt={6}
                  onClick={() => {
                    onChangeNoteDraft(customer.note || '')
                    onSetNoteEditing(true)
                  }}
                >
                  Изменить примечание
                </Button>
              </Alert>
            ) : (
              <Button
                size="compact-xs"
                variant="default"
                mb={6}
                onClick={() => {
                  onChangeNoteDraft('')
                  onSetNoteEditing(true)
                }}
              >
                Добавить примечание
              </Button>
            )}
          </>
        )}
      </Collapse>
    </Card>
  )
}
